import { FINANCIAL_COLORS } from '@components/charts/chartConstants';
import type {
  DividendMatrixTableData,
  DividendTableCell,
  DividendTableRow,
  PerformanceDividendData,
} from '@app-types/performanceDividend';

const c = (text: string, align: DividendTableCell['align'] = 'right'): DividendTableCell => ({
  text,
  align,
  nowrap: true,
});

const DATE_PLACEHOLDER = 'YYYY/MM/DD';

type TrendSample = {
  label: string;
  perShare: string;
  annual: string;
  payoutRatio: string;
  variant?: DividendTableRow['variant'];
  disclosure?: DividendTableRow['disclosure'];
  nodeId?: string;
};

const trendSamples: TrendSample[] = [
  {
    label: '2025/03予',
    perShare: '100,000円',
    annual: '200,000円',
    payoutRatio: '40％',
    variant: 'header-like',
    disclosure: 'up',
    nodeId: '3135:4125',
  },
  {
    label: '2024/12',
    perShare: '33,333円',
    annual: '-',
    payoutRatio: '-',
    disclosure: 'up',
    nodeId: '3579:26817',
  },
  {
    label: '2024/09',
    perShare: '33,333円',
    annual: '-',
    payoutRatio: '-',
    disclosure: 'up',
    nodeId: '3579:26851',
  },
  {
    label: '2024/06',
    perShare: '33,333円',
    annual: '-',
    payoutRatio: '-',
    disclosure: 'up',
    nodeId: '3579:26885',
  },
  {
    label: '2024/03予',
    perShare: '100,000円',
    annual: '200,000円',
    payoutRatio: '40％',
    variant: 'header-like',
    disclosure: 'down',
    nodeId: '3135:4126',
  },
  {
    label: '2023/03',
    perShare: '100,000円',
    annual: '200,000円',
    payoutRatio: '40％',
    variant: 'header-like',
    disclosure: 'down',
    nodeId: '3135:4127',
  },
  {
    label: '2022/03',
    perShare: '100,000円',
    annual: '200,000円',
    payoutRatio: '40％',
    variant: 'header-like',
    disclosure: 'down',
    nodeId: '3135:4128',
  },
  {
    label: '2021/03',
    perShare: '100,000円',
    annual: '200,000円',
    payoutRatio: '40％',
    variant: 'header-like',
    disclosure: 'down',
    nodeId: '3135:4129',
  },
  {
    label: '2020/03',
    perShare: '100,000円',
    annual: '200,000円',
    payoutRatio: '40％',
    variant: 'header-like',
    disclosure: 'down',
    nodeId: '3135:4130',
  },
  {
    label: '2019/03',
    perShare: '100,000円',
    annual: '200,000円',
    payoutRatio: '40％',
    variant: 'header-like',
    disclosure: 'down',
    nodeId: '3135:4131',
  },
  {
    label: '2018/03',
    perShare: '100,000円',
    annual: '200,000円',
    payoutRatio: '40％',
    variant: 'header-like',
    disclosure: 'down',
    nodeId: '3135:4132',
  },
  {
    label: '2017/03',
    perShare: '100,000円',
    annual: '200,000円',
    payoutRatio: '40％',
    variant: 'header-like',
    disclosure: 'down',
    nodeId: '3135:4133',
  },
  {
    label: '2016/03',
    perShare: '100,000円',
    annual: '200,000円',
    payoutRatio: '40％',
    variant: 'header-like',
    disclosure: 'down',
    nodeId: '3135:4134',
  },
];

const trendRows: DividendTableRow[] = trendSamples.map((item) => ({
  key: item.label,
  label: item.label,
  variant: item.variant,
  disclosure: item.disclosure,
  nodeId: item.nodeId,
  cells: [
    c(item.perShare),
    c(item.annual),
    c(item.payoutRatio),
    c(DATE_PLACEHOLDER),
    c(DATE_PLACEHOLDER),
    c(DATE_PLACEHOLDER),
    c(DATE_PLACEHOLDER),
  ],
}));

const existingInterimNodeIds = new Set(['3579:26817', '3579:26851', '3579:26885']);
const existingInterimRows = trendRows
  .filter((row) => row.nodeId && existingInterimNodeIds.has(row.nodeId))
  .map((row) => ({
    ...row,
    variant: 'default' as const,
    disclosure: undefined,
  }));

const annualRows = trendRows.filter(
  (row) => !row.nodeId || !existingInterimNodeIds.has(row.nodeId),
);

const buildInterimRows = (row: DividendTableRow): DividendTableRow[] => {
  if (row.nodeId === '3135:4125') {
    return existingInterimRows;
  }

  const yearMatch = row.label.match(/^(\d{4})\/03/);
  const fiscalYear = yearMatch ? Number(yearMatch[1]) - 1 : null;
  if (!fiscalYear) {
    return [];
  }

  return ['12', '09', '06'].map((month) => ({
    key: `${row.key}-${fiscalYear}/${month}`,
    label: `${fiscalYear}/${month}`,
    variant: 'default' as const,
    cells: [
      c('33,333円'),
      c('-'),
      c('-'),
      c(DATE_PLACEHOLDER),
      c(DATE_PLACEHOLDER),
      c(DATE_PLACEHOLDER),
      c(DATE_PLACEHOLDER),
    ],
  }));
};

const annualTrendRows: DividendTableRow[] = annualRows.map((row) => {
  const children = buildInterimRows(row);
  return {
    ...row,
    disclosure: children.length > 0 ? 'down' : undefined,
    children,
  };
});

const trendsTable: DividendMatrixTableData = {
  nodeId: '3135:4123',
  caption: '配当金履歴',
  minWidth: 851,
  mobileMinWidth: 851,
  labelColumnWidth: 144,
  columns: [
    { key: 'label', label: ['配当金履歴'], width: 144, align: 'left', nowrap: true },
    { key: 'perShare', label: ['一株配当'], width: 100, align: 'right', nowrap: true },
    { key: 'annual', label: ['年間配当'], width: 100, align: 'right', nowrap: true },
    { key: 'payoutRatio', label: ['配当性向'], width: 100, align: 'right', nowrap: true },
    { key: 'recordDate', label: ['配当基準日'], width: 100, align: 'right', nowrap: true },
    { key: 'exDate', label: ['権利落ち日'], width: 100, align: 'right', nowrap: true },
    { key: 'payDate', label: ['配当支払日'], width: 100, align: 'center', nowrap: true },
    { key: 'updatedDate', label: ['データ更新日'], width: 100, align: 'center', nowrap: true },
  ],
  rows: annualTrendRows,
};

const historyRows: DividendTableRow[] = [
  {
    key: '2026/03',
    label: '2026/03(予)',
    variant: 'header-like',
    nodeId: '3490:22496',
    cells: [c('95.00'), c('-'), c('2026/03/31'), c('2026/03/30'), c('-'), c('2025/10/29')],
  },
  {
    key: '2025/03',
    label: '2025/03',
    variant: 'header-like',
    nodeId: '3490:22497',
    cells: [c('107.00'), c('190.00'), c('2025/03/31'), c('2025/03/28'), c('2025/06/20'), c('2025/04/28')],
  },
  {
    key: '2024/09',
    label: '2024/09',
    variant: 'header-like',
    nodeId: '3490:22498',
    cells: [c('83.00'), c('-'), c('2024/09/30'), c('2024/09/27'), c('2024/12/02'), c('2025/01/31')],
  },
  {
    key: '2024/03',
    label: '2024/03',
    variant: 'header-like',
    nodeId: '3490:22499',
    cells: [c('95.00'), c('167.00'), c('2024/03/31'), c('2024/03/28'), c('2024/06/20'), c('2024/04/26')],
  },
];

const historyTable: DividendMatrixTableData = {
  nodeId: '3490:22493',
  caption: '配当履歴',
  minWidth: 624,
  mobileMinWidth: 624,
  columns: [
    { key: 'label', label: [''], align: 'left', nowrap: true },
    { key: 'perShare', label: ['一株配当(円)'], align: 'right', nowrap: true },
    { key: 'annual', label: ['年間配当(円)'], align: 'right', nowrap: true },
    { key: 'recordDate', label: ['配当基準日'], align: 'right', nowrap: true },
    { key: 'exDate', label: ['権利落ち日'], align: 'right', nowrap: true },
    { key: 'payDate', label: ['配当支払日'], align: 'right', nowrap: true },
    { key: 'updatedDate', label: ['データ', '更新日'], align: 'right', nowrap: true },
  ],
  rows: historyRows,
};

export const mockPerformanceDividend: PerformanceDividendData = {
  dividendInfo: {
    title: '配当情報',
    sectionNodeId: '3132:3262',
    headerNodeId: '3132:3263',
    tableNodeId: '3132:3264',
    rows: [
      {
        key: 'actualDividendAmount',
        label: '実績配当金額',
        value: '100円',
        nodeId: '3132:3265',
      },
      {
        key: 'forecastDividendAmount',
        label: '予想配当金額',
        value: '250円',
        nodeId: '3132:3266',
      },
      {
        key: 'forecastDividendYield',
        label: '予想配当利回り',
        value: '3.05％',
        nodeId: '3132:3267',
      },
      {
        key: 'payoutRatio',
        label: '配当性向',
        value: '40％',
        nodeId: '3132:3268',
      },
      {
        key: 'doe',
        label: 'DOE',
        value: '4％',
        nodeId: '3132:3269',
      },
    ],
  },
  relatedSchedule: {
    title: '関連スケジュール',
    sectionNodeId: '3132:3270',
    headerNodeId: '3132:3271',
    tableNodeId: '3132:3272',
    rows: [
      {
        key: 'lastTradingDateWithRights',
        label: '権利付き最終日',
        value: DATE_PLACEHOLDER,
        nodeId: '3132:3273',
      },
      {
        key: 'exDividendDate',
        label: '権利落ち日',
        value: DATE_PLACEHOLDER,
        nodeId: '3132:3274',
      },
      {
        key: 'recordDate',
        label: '権利確定日',
        value: DATE_PLACEHOLDER,
        nodeId: '3132:3275',
      },
      {
        key: 'paymentDate',
        label: '配当支払い日',
        value: DATE_PLACEHOLDER,
        nodeId: '3132:3276',
      },
    ],
  },
  trends: {
    title: '配当推移',
    sectionNodeId: '3135:4120',
    headerNodeId: '3490:22490',
    panelNodeId: '3490:22287',
    chartCardNodeId: '3632:38409',
    legendNodeId: '3632:38469',
    footerNodeId: '3490:23028',
    footerLogoNodeId: '3490:23029',
    controls: {
      selectedPeriod: '10',
      periodOptions: [
        { value: '10', label: '直近10期' },
        { value: '5', label: '直近5期' },
      ],
    },
    chart: {
      nodeId: '3353:29861',
      yAxisLeftLabel: '配当(円)',
      yAxisRightLabel: '株価(円)',
      dividendSeriesName: '調整済み配当金',
      stockSeriesName: 'コマツ',
      dividendColor: FINANCIAL_COLORS.red,
      stockColor: FINANCIAL_COLORS.blue,
      leftAxisMax: 200,
      leftAxisInterval: 50,
      rightAxisMin: 0,
      rightAxisMax: 5000,
      rightAxisInterval: 1000,
      points: [
        { label: '16/03', dividend: 34, stockPrice: 1800 },
        { label: '17/03', dividend: 58, stockPrice: 2100 },
        { label: '18/03', dividend: 84, stockPrice: 2700 },
        { label: '19/03', dividend: 110, stockPrice: 2500 },
        { label: '20/03', dividend: 94, stockPrice: 1800 },
        { label: '21/03', dividend: 55, stockPrice: 2200 },
        { label: '22/03', dividend: 96, stockPrice: 2800 },
        { label: '23/03', dividend: 139, stockPrice: 3400 },
        { label: '24/03', dividend: 167, stockPrice: 4300 },
        { label: '25/03', dividend: 190, stockPrice: 4700 },
      ],
    },
    legend: [
      { key: 'dividend', label: '調整済み配当金', color: FINANCIAL_COLORS.red, type: 'box' },
      { key: 'stock', label: 'コマツ', color: FINANCIAL_COLORS.blue, type: 'line' },
    ],
    trendsTable,
    historyTable,
  },
};

export default mockPerformanceDividend;
